import { useEffect, useState } from "react";

// Hours in Mountain Time (Lethbridge, AB / America/Edmonton).
// Index 0 = Sunday, 6 = Saturday. null = closed. Physical close is 10:00 PM
// daily; online orders cut off 30 minutes earlier (see /app/backend/hours.py).
const SCHEDULE = [
  { open: "11:00", close: "22:00" }, // Sun
  { open: "11:00", close: "22:00" }, // Mon
  { open: "16:00", close: "22:00" }, // Tue — opens 4 PM
  { open: "11:00", close: "22:00" }, // Wed
  { open: "11:00", close: "22:00" }, // Thu
  { open: "11:00", close: "22:00" }, // Fri
  { open: "11:00", close: "22:00" }, // Sat
];

const TZ = "America/Edmonton";
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function parseHHMM(s) {
  const [h, m] = s.split(":").map((n) => parseInt(n, 10));
  return h * 60 + m;
}

function toAmPm(hhmm) {
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return m === 0 ? `${h12} ${ampm}` : `${h12}:${m.toString().padStart(2, "0")} ${ampm}`;
}

/** Returns { open: bool, day: 0..6, minutes: number } for "now" in Lethbridge. */
function nowInLethbridge() {
  // Format current UTC in TZ, then read the wall-clock parts back.
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(new Date());
  const wd = parts.find((p) => p.type === "weekday")?.value || "Sun";
  const hour = parseInt(parts.find((p) => p.type === "hour")?.value || "0", 10);
  const min = parseInt(parts.find((p) => p.type === "minute")?.value || "0", 10);
  const day = DAY_NAMES.indexOf(wd);
  return { day: day < 0 ? 0 : day, minutes: hour * 60 + min };
}

function computeStatus() {
  const { day, minutes } = nowInLethbridge();
  const today = SCHEDULE[day];
  if (today) {
    const openMin = parseHHMM(today.open);
    const closeMin = parseHHMM(today.close);
    if (minutes >= openMin && minutes < closeMin) {
      return {
        isOpen: true,
        label: `Open · closes ${toAmPm(today.close)}`,
      };
    }
    if (minutes < openMin) {
      return {
        isOpen: false,
        label: `Closed · opens ${toAmPm(today.open)} today`,
      };
    }
  }
  // Closed for the rest of today — find next open day
  for (let i = 1; i <= 7; i++) {
    const idx = (day + i) % 7;
    const sched = SCHEDULE[idx];
    if (sched) {
      const dayLabel = i === 1 ? "tomorrow" : DAY_NAMES[idx];
      return {
        isOpen: false,
        label: `Closed · opens ${toAmPm(sched.open)} ${dayLabel}`,
      };
    }
  }
  return { isOpen: false, label: "Closed" };
}

/**
 * Live "Open Now" / "Closed" pill.
 *
 * Variants:
 *   - dark  (default): glass-pill on dark backgrounds (header)
 *   - light: solid pill for light backgrounds (contact, hero)
 *   - inline: tiny inline text (footer)
 */
export default function OpenNowBadge({ variant = "dark", className = "" }) {
  const [status, setStatus] = useState(() => computeStatus());

  useEffect(() => {
    const id = setInterval(() => setStatus(computeStatus()), 60000);
    return () => clearInterval(id);
  }, []);

  const { isOpen, label } = status;

  if (variant === "inline") {
    return (
      <span
        data-testid="open-now-badge"
        data-open={isOpen}
        className={`inline-flex items-center gap-1.5 text-xs font-bold ${
          isOpen ? "text-[#5BBF6A]" : "text-[#A6692D]"
        } ${className}`}
      >
        <span
          className={`relative inline-flex w-1.5 h-1.5 rounded-full ${
            isOpen ? "bg-[#5BBF6A]" : "bg-[#A6692D]"
          }`}
        >
          {isOpen && (
            <span className="absolute inset-0 rounded-full bg-[#5BBF6A] animate-ping opacity-70" />
          )}
        </span>
        {label}
      </span>
    );
  }

  if (variant === "light") {
    return (
      <span
        data-testid="open-now-badge"
        data-open={isOpen}
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold tracking-wide ${
          isOpen
            ? "bg-[#5BBF6A]/10 text-[#3D8C49] border border-[#5BBF6A]/40"
            : "bg-[#A6692D]/10 text-[#A6692D] border border-[#A6692D]/30"
        } ${className}`}
      >
        <span className="relative inline-flex w-2 h-2 rounded-full bg-current">
          {isOpen && (
            <span className="absolute inset-0 rounded-full bg-current animate-ping opacity-60" />
          )}
        </span>
        {label}
      </span>
    );
  }

  // dark = on glass header
  return (
    <span
      data-testid="open-now-badge"
      data-open={isOpen}
      className={`inline-flex items-center gap-1.5 glass-pill rounded-full px-3 py-1.5 text-[11px] font-bold tracking-wide ${
        isOpen ? "text-[#A9F0B5]" : "text-[#E5B07D]"
      } ${className}`}
    >
      <span
        className={`relative inline-flex w-2 h-2 rounded-full ${
          isOpen ? "bg-[#5BBF6A]" : "bg-[#E5B07D]"
        }`}
      >
        {isOpen && (
          <span className="absolute inset-0 rounded-full bg-[#5BBF6A] animate-ping opacity-70" />
        )}
      </span>
      {label}
    </span>
  );
}
